import React, { useEffect, useMemo, useState } from "react";

// region Languages
import useTranslation from "src/translations/useTranslation";
import { CanConsumptionMessages } from "@shared/languages/interfaces";
// endregion Languages
// region Shared
import { FilterApplicationTypesID } from "@shared/constants/filter-application-types.enum";
import { ParamNameTypes } from "@shared/constants/param-name-types.enum";
import { IParamQueryValues } from "@shared/interfaces/filter.interface";
import { VehicleType } from "@shared/interfaces/vehicle-type.interface";
import { Plant } from "@shared/entities/reg_plants.entity";
// endregion Shared
// region Services
import api from "@services/api";
// endregion Services
// region Hooks
import { useToast } from "@hooks/useToast";
// endregion Hooks
// region Utils
import utils from "@utils/useful-functions";
// endregion Utils
// region Organisms
import GenericQueryFilter, {
  IDateRangeOptions,
  IDateRangeQuickRange,
  IMultipleSelectionOptions,
  IOnFilterReturn,
  IOptionValue
} from "@organisms/GenericQueryFilter";
// endregion Organisms
// region Page
import { vehicleTypeLabel } from "../can-consumption-metrics";
import { DEFAULT_PERIOD_DAYS, fromDateTimeRange, periodEndingOnLastCompleteDay, toDateTimeRange } from "../can-consumption-period";
import { CanConsumptionDashboardFilter, CanConsumptionDashboardFilterOption } from "../can-consumption.types";
// endregion Page

/** The endpoint takes the plants as travel origins; there is no `ParamNameTypes` entry for it. */
const PARAM_NAME_ORIGINS = "idOrigins";

/**
 * Shortcuts offered above the date fields of the period block.
 *
 * They are resolved here, never inside the shared card: every one of them ends on the last day
 * considered complete (D-2), never on today, because the invoices of the last days have not arrived
 * yet and the consumption per m³ of those days is inflated.
 */
const PERIOD_PRESETS = [
  { days: DEFAULT_PERIOD_DAYS, labelKey: CanConsumptionMessages.filterPreset7Days },
  { days: 3, labelKey: CanConsumptionMessages.filterPreset3Days },
  { days: 1, labelKey: CanConsumptionMessages.filterPreset1Day }
];

interface QueryFilterCanConsumptionDashboardProps {
  open: boolean;
  onClose: () => void;
  /** Filter the panel is showing, so reopening the drawer starts from what is on screen */
  appliedFilter: CanConsumptionDashboardFilter;
  onFilter: (filter: CanConsumptionDashboardFilter) => void;
}

/**
 * Filter drawer of the CAN consumption panel.
 *
 * The panel is a screen, never a file: `onFilter` never answers `scheduledReport` and no export
 * option is offered, so the drawer only ever applies the filter and closes.
 */
const QueryFilterCanConsumptionDashboard: React.FC<QueryFilterCanConsumptionDashboardProps> = ({
  open, onClose, appliedFilter, onFilter
}) => {

  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  // endregion Hooks
  // region States
  const [plants, setPlants] = useState<Plant[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [loadingPlants, setLoadingPlants] = useState(true);
  const [loadingVehicleTypes, setLoadingVehicleTypes] = useState(true);
  // endregion States
  // region Constants
  const plantFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingPlants,
    labelName: t(CanConsumptionMessages.filterPlants),
    paramName: PARAM_NAME_ORIGINS,
    values: plants
      .filter((plant) => plant.location?.id_location)
      .map((plant) => ({ value: plant.location!.id_location!, label: plant.name! }))
  }), [loadingPlants, plants, t]);

  const vehicleTypeFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingVehicleTypes,
    labelName: t(CanConsumptionMessages.filterVehicleTypes),
    paramName: ParamNameTypes.ID_VEHICLE_TYPES,
    values: vehicleTypes.map((vehicleType) => ({
      value: vehicleType.id_vehicle_type!,
      label: vehicleTypeLabel(vehicleType.id_vehicle_type, vehicleType.description, "plural", t) ?? ""
    }))
  }), [loadingVehicleTypes, vehicleTypes, t]);

  const appliedRange = toDateTimeRange(appliedFilter.period);

  // Resolved on the click, so a panel left open past midnight still moves to the right window.
  const quickRanges: IDateRangeQuickRange[] = useMemo(() => PERIOD_PRESETS.map((preset) => ({
    label: t(preset.labelKey),
    resolveValues: () => {
      const range = toDateTimeRange(periodEndingOnLastCompleteDay(preset.days));

      return { startDate: range.startDate, endDate: range.finishDate };
    }
  })), [t]);

  const dateRangeOptions: IDateRangeOptions = {
    labelName: t(CanConsumptionMessages.filterPeriod),
    paramNameStartDate: ParamNameTypes.START_DATE,
    paramNameEndDate: ParamNameTypes.FINISH_DATE,
    defaultValues: { startDate: appliedRange.startDate, endDate: appliedRange.finishDate },
    quickRanges
  };

  const defaultAppliedFilterOptions: IParamQueryValues[] = [
    { paramName: ParamNameTypes.START_DATE, paramValue: appliedRange.startDate },
    { paramName: ParamNameTypes.FINISH_DATE, paramValue: appliedRange.finishDate },
    { paramName: PARAM_NAME_ORIGINS, paramValue: appliedFilter.origins.map((origin) => origin.id) },
    { paramName: ParamNameTypes.ID_VEHICLE_TYPES, paramValue: appliedFilter.vehicleTypes.map((type) => type.id) }
  ];
  // endregion Constants
  // region Functions
  /**
   * Load the plant options from the API.
   */
  const loadPlantOptions = async () => {
    try {
      const { data } = await api.get("plants/read", { params: { linkedLocation: true } });

      setPlants(data.status === "success" ? (data.result as Plant[]) : []);
    } catch (error: any) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingPlants(false);
    }
  };

  /**
   * Load the vehicle type options from the API.
   */
  const loadVehicleTypeOptions = async () => {
    try {
      const { data } = await api.get("vehicles/types/read", { params: { translate: true } });

      setVehicleTypes(data.status === "success" ? (data.result as VehicleType[]) : []);
    } catch (error: any) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingVehicleTypes(false);
    }
  };

  /**
   * Name the selected ids, so the panel can say what is filtered without holding the option lists.
   * @param ids Selected values
   * @param options Options the drawer offered for them
   */
  const describeSelection = (ids: string[], options: IOptionValue[]): CanConsumptionDashboardFilterOption[] => (
    ids.map((id) => ({ id, label: options.find((option) => option.value === id)?.label ?? id }))
  );

  /**
   * Turn what the drawer collected into the filter of the panel and hand it to the page.
   * @param paramsData Parameters the drawer applied, already without the empty ones
   */
  const handleFilter = async (paramsData: IParamQueryValues[]): Promise<IOnFilterReturn> => {
    const valueOf = (paramName: string) => paramsData.find((param) => param.paramName === paramName)?.paramValue;

    const startDate = (valueOf(ParamNameTypes.START_DATE) as string) ?? appliedRange.startDate;
    const finishDate = (valueOf(ParamNameTypes.FINISH_DATE) as string) ?? appliedRange.finishDate;

    onFilter({
      period: fromDateTimeRange(startDate, finishDate),
      origins: describeSelection((valueOf(PARAM_NAME_ORIGINS) as string[]) ?? [], plantFilterOptions.values),
      vehicleTypes: describeSelection(
        (valueOf(ParamNameTypes.ID_VEHICLE_TYPES) as string[]) ?? [],
        vehicleTypeFilterOptions.values
      )
    });

    return { status: "success" };
  };
  // endregion Functions
  // region Effects

  // Load the drawer options from the API
  useEffect(() => {
    Promise.all([loadPlantOptions(), loadVehicleTypeOptions()]).then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // endregion Effects

  return (
    <GenericQueryFilter
      open={open}
      onClose={onClose}
      applicationTypeID={FilterApplicationTypesID.CAN_CONSUMPTION_DASHBOARD}
      defaultAppliedFilterOptions={defaultAppliedFilterOptions}
      onFilter={(paramsData) => handleFilter(paramsData)}
      multipleSelectionOptions={[plantFilterOptions, vehicleTypeFilterOptions]}
      dateRangeOptions={dateRangeOptions}
    />
  );
};

export default QueryFilterCanConsumptionDashboard;
